import React, { useEffect, useState, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

/* Molecules */
import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'

/* Organisms */
import EditExportTableMenu from 'components_new/organisms/EditExportTableMenu'
import ExportTableOrganism from 'components_new/organisms/ExportTable'
import WidgetSettings from 'components_new/organisms/Widget/WidgetSettings'
import { ExportTableShareDialog } from 'components_new/organisms/dialogs/ExportTableShareDialog'

import PageLayout from 'components_new/templates/PageLayout'

/* Actions */
import { useExportTableActions } from 'redux/actions/ExportTables'
import { useExportTableWidgetActions } from 'redux/actions/Widgets/ExportTableWidgets'
import { useWidgetActions } from 'redux/actions/Widgets'

/* Hooks */
import { useExportTableIncludeWidget } from 'redux/hooks/ExportTables'
import { useAllowAccessExportTables } from 'redux/hooks/Permissions'

/* Types */
import {
  InnerWidgetPatchBody,
  isExportTableWidgetFullyLoaded,
  isExportTableWidgetSettingsLoaded
} from 'types/GlobalWidget'
import { formatWidgetData, isFormattedData } from 'utils/widgetData'

const ExportTable = () => {
  const { t, lang } = useTranslation()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const allowAccess = useAllowAccessExportTables()
  const exportTable = useExportTableIncludeWidget(id)
  const { tryGetOneExportTable } = useExportTableActions()
  const { tryExportWidgetDataXlsx } = useWidgetActions()
  const { tryUpdateExportTableWidget } = useExportTableWidgetActions()

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  useEffect(() => {
    if (id) {
      tryGetOneExportTable(id)
    }
  }, [id])

  useEffect(() => {
    if (id && exportTable?.widget && !exportTable.widget.data) {
      tryGetOneExportTable(id)
    }
  }, [exportTable?.widget?.dataVersion])

  // Format widget data for rendering and export
  const formattedData = useMemo(() => {
    if (
      exportTable &&
      exportTable.widget &&
      isExportTableWidgetFullyLoaded(exportTable.widget)
    ) {
      return formatWidgetData(
        exportTable.widget.data,
        exportTable.widget.settings,
        lang
      )
    }

    return null
  }, [exportTable])

  if (!allowAccess) {
    return (
      <ErrorPlaceholder
        code={401}
        body={t('export_tables', 'no_access_body')}
        actions={
          <Button
            variant="text"
            onClick={() => history.push('/')}
            startIcon={<Icon name="ArrowBackOutlined" />}
          >
            {t('global', 'home')}
          </Button>
        }
      />
    )
  }

  if (!exportTable) {
    return null
  }

  const editDisabled =
    !exportTable.widget ||
    !isExportTableWidgetSettingsLoaded(exportTable.widget)

  const exportDisabled =
    !exportTable.widget ||
    !isExportTableWidgetFullyLoaded(exportTable.widget) ||
    !isFormattedData(formattedData)

  const handleExport = () => {
    if (
      exportTable.widget &&
      isExportTableWidgetFullyLoaded(exportTable.widget) &&
      isFormattedData(formattedData)
    ) {
      tryExportWidgetDataXlsx(
        exportTable.widget.settings,
        formattedData,
        exportTable.title,
        t('widgets', 'total_column'),
        t('widgets', 'variant_options_row_header')
      )
    }
  }

  const updateWidget = (body: InnerWidgetPatchBody) => {
    if (exportTable.widget) {
      tryUpdateExportTableWidget(exportTable.id, exportTable.widget.id, {
        data: body
      })
    }
  }

  return (
    <PageLayout
      variant="static"
      content={{
        disablePadding: true,
        fullWidth: true
      }}
      topbar={{
        actions: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('global', 'export')}>
              <IconButton onClick={handleExport} disabled={exportDisabled}>
                <Icon name="Download" />
              </IconButton>
            </Tooltip>
            <Button
              onClick={() => setSettingsOpen(true)}
              variant="text"
              disabled={editDisabled}
            >
              {t('global', 'edit')}
            </Button>
            <Button
              onClick={() => setShareOpen(true)}
              startIcon={<Icon name="Share" />}
              variant="outlined"
            >
              {t('global', 'share')}
            </Button>
            <Divider orientation="vertical" flexItem />
            <EditExportTableMenu
              editDisabled={editDisabled}
              exportDisabled={exportDisabled}
              exportTable={exportTable}
              onEdit={() => setSettingsOpen(true)}
              onExport={handleExport}
            />
          </Box>
        ),
        breadcrumbs: [
          {
            href: '/export-tables',
            icon: { name: 'Table' },
            title: t('export_tables', 'all_tables')
          },
          {
            title: exportTable.title
          }
        ]
      }}
    >
      <Box sx={{ height: '100%', width: '100%' }}>
        <ExportTableOrganism
          formattedData={formattedData}
          widget={exportTable.widget}
        />
      </Box>

      {exportTable.widget ? (
        <WidgetSettings
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          updateWidget={updateWidget}
          widget={exportTable.widget}
        />
      ) : null}

      <ExportTableShareDialog
        exportTable={exportTable}
        onClose={() => setShareOpen(false)}
        open={shareOpen}
      />
    </PageLayout>
  )
}

export default ExportTable
